import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const PROFILE_TABS = [
  { type: "CLIENT", label: "Klienci", candidatesKey: "clients" },
  { type: "TEACHER", label: "Nauczyciele", candidatesKey: "teachers" },
];

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesSearch(candidate, searchText) {
  if (!searchText) return true;
  return [candidate.label, candidate.email].some(value => normalizeText(value).includes(searchText));
}

function getCandidateText(candidate) {
  return [candidate.label, candidate.email].filter(Boolean).join(" ");
}

function CreateAccountModal({
  accountCandidates,
  error,
  onClose,
  onCreate,
}) {
  const [activeProfileType, setActiveProfileType] = useState("CLIENT");
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const activeTab = PROFILE_TABS.find(tab => tab.type === activeProfileType) || PROFILE_TABS[0];
  const candidates = accountCandidates[activeTab.candidatesKey] || [];
  const filteredCandidates = useMemo(() => {
    const normalizedSearch = normalizeText(searchText);
    return candidates.filter(candidate => matchesSearch(candidate, normalizedSearch));
  }, [candidates, searchText]);

  useEffect(() => {
    setSearchText("");
    setSelectedId("");
    setLocalError("");
  }, [activeProfileType]);

  const validate = () => {
    if (!selectedId) {
      setLocalError("Wybierz profil.");
      return false;
    }
    if (temporaryPassword.length < 8) {
      setLocalError("Haslo musi miec co najmniej 8 znakow.");
      return false;
    }
    setLocalError("");
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    onCreate({
      profileType: activeProfileType,
      profileId: selectedId,
      password: temporaryPassword,
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog" aria-modal="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Utworz konto</h5>
            <button type="button" className="close" onClick={onClose} aria-label="Zamknij">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {(localError || error) && (
                <div className="alert alert-danger" role="alert">{localError || error}</div>
              )}
              <div className="btn-group mb-3" role="tablist" aria-label="Typ profilu">
                {PROFILE_TABS.map(tab => (
                  <button
                    key={tab.type}
                    type="button"
                    className={`btn ${activeProfileType === tab.type ? "manage-red-button" : "manage-teal-button"}`}
                    onClick={() => setActiveProfileType(tab.type)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label htmlFor="create-account-search">
                  Szukaj
                  <input
                    id="create-account-search"
                    className="form-control"
                    value={searchText}
                    onChange={event => setSearchText(event.target.value)}
                  />
                </label>
              </div>
              <div className="list-group mb-3">
                {filteredCandidates.map(candidate => (
                  <label className="list-group-item" key={candidate.id} htmlFor={`candidate-${candidate.id}`}>
                    <input
                      id={`candidate-${candidate.id}`}
                      className="mr-2"
                      type="radio"
                      name="accountCandidate"
                      value={candidate.id}
                      checked={selectedId === candidate.id}
                      aria-label={getCandidateText(candidate)}
                      onChange={event => setSelectedId(event.target.value)}
                    />
                    <span>{candidate.label}</span>
                    {candidate.email && <small className="ml-2 text-muted">{candidate.email}</small>}
                  </label>
                ))}
                {filteredCandidates.length === 0 && <div className="text-muted">Brak wynikow</div>}
              </div>
              <div className="form-group">
                <label htmlFor="temporary-password">
                  Haslo tymczasowe
                  <input
                    id="temporary-password"
                    className="form-control"
                    type="password"
                    value={temporaryPassword}
                    onChange={event => setTemporaryPassword(event.target.value)}
                  />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={onClose}>
                Anuluj
              </button>
              <button type="submit" className="btn manage-button manage-red-button">
                Utworz konto
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

CreateAccountModal.propTypes = {
  accountCandidates: PropTypes.shape({
    clients: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      email: PropTypes.string,
    })),
    teachers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      email: PropTypes.string,
    })),
  }).isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

CreateAccountModal.defaultProps = {
  error: "",
};

export default CreateAccountModal;
